import { Fragment, ReactNode, useEffect, useMemo, useState } from "react";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AddIcon from "@mui/icons-material/Add";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import EditNoteOutlinedIcon from "@mui/icons-material/EditNoteOutlined";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import EventOutlinedIcon from "@mui/icons-material/EventOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import LocalOfferOutlinedIcon from "@mui/icons-material/LocalOfferOutlined";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import ViewColumnIcon from "@mui/icons-material/ViewColumn";
import {
  Alert, Autocomplete, Box, Button, Checkbox, CircularProgress, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, IconButton, InputAdornment, List, ListItemButton, ListItemText, Menu, MenuItem, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, TextField, Tooltip, Typography,
} from "@mui/material";
import { useGetQuoteProducts } from "../../api/useProducts";
import { useQuoteLinePromotions } from "../../api/useQuotes";
import QuoteDatesModal from "../QuoteDatesModal/QuoteDatesModal";
import PromotionsCatalogDialog from "../PromotionsCatalogDialog/PromotionsCatalogDialog";
import { emptySectionDates } from "../QuoteSectionBar/sectionUtils";
import { formatIsoDate, pastFirstTermWarning } from "../../shared/quoteDate";
import { IQuoteLineItem, IQuoteProduct, PromotionCatalogRow, PromotionPrerequisiteStatus, QuoteLinePromotion, QuoteSegment, QuoteTerm } from "../../shared/types";
import { calculateManualLinePricing, calculateQuoteLinePricing, filterQuoteProducts, promotionBigIdKey, promotionSkuKey, quoteProductsForSegment, quoteProductTerm, QuoteDiscounts, QuoteProductChoice } from "./quoteLineItemUtils";
import "./QuoteLineItemGrid.scss";

const allSegments: QuoteSegment[] = ["Commercial", "Charity", "Education", "Government"];
const money = (value: number | null | undefined) => value == null ? "" : value.toLocaleString(undefined, {
  style: "currency", currency: "USD", minimumFractionDigits: 2,
});
const unitMoney = (value: number | null | undefined) => value == null ? "" : value.toLocaleString(undefined, {
  style: "currency", currency: "USD", minimumFractionDigits: 2, maximumFractionDigits: 2,
});
const displayDate = (value: string | null | undefined) => {
  if (!value) return "";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "" : date.toLocaleDateString();
};
const productChoiceLabel = (choice: QuoteProductChoice) => choice.sku_title;
// A promotion's eligibility detail as a reason worth showing. Lines saved before 2026-10-05 may
// carry the old placeholder "not verified - confirm in Partner Center", which only restated the
// state itself - dropped rather than shown as "eligibility not verified: not verified - ...".
const eligibilityReason = (detail: string | null | undefined) =>
  detail && !/^not verified - confirm in Partner Center$/i.test(detail) ? detail : null;

// Issues 23/38/36: plain-language text for a line whose applied Discount fell short of the
// requested Discount. Distinguishes a Cost-floor cap from a product restriction (a Calling Plan).
// For an automatic line the requested Discount is the standard term Discount; for a manual line
// (Issue 36) it is the percentage the user typed.
const adjustmentMessage = (reason: string | null | undefined, manual = false) => reason === "margin_rule"
  ? manual ? "The entered discount was reduced so Sale stays at or above Cost." : "The standard discount was capped by the margin rule."
  : reason === "product_restriction"
    ? manual ? "The discount was not applied because this product is restricted (Calling Plan)." : "The standard discount was not applied because this product is restricted."
    : null;

const optionalColumns = [
  ["segment", "Segment"], ["term", "Term"], ["msrp", "MSRP"], ["cost", "Cost"],
  ["discount", "Discount"], ["promo", "Promo"], ["sale", "Sale"], ["total", "Total"], ["margin", "Margin"], ["dates", "Dates"], ["actions", "Actions"],
] as const;
type OptionalColumn = typeof optionalColumns[number][0];
const columnPreferenceKey = "quick_quote_visible_columns";
// Dates and Discount start hidden for a brand-new browser - Dates
// (which also folds in Renewal; the two used to be separate columns) has its editing action in
// the Actions menu, not a column button, and most lines just take the standard discount for their
// term (the Discount column only earns its space back when a line's discount was actually adjusted,
// which already gets its own inline highlight/row message regardless of this column's
// visibility). A returning user who already has an explicit stored preference (even one that
// predates either change and so still includes them) keeps whatever they already had.
const defaultOptionalColumns = () => new Set<OptionalColumn>(optionalColumns.map(([key]) => key).filter(key => key !== "dates" && key !== "discount"));
const readVisibleColumns = () => {
  try {
    const stored = JSON.parse(localStorage.getItem(columnPreferenceKey) || "null");
    if (![1, 2, 3, 4, 5, 6].includes(stored?.version) || !Array.isArray(stored.columns)) return defaultOptionalColumns();
    const recognized = new Set<OptionalColumn>();
    stored.columns.forEach((key: unknown) => {
      if (optionalColumns.some(([candidate]) => candidate === key)) recognized.add(key as OptionalColumn);
    });
    if (stored.version === 1) recognized.add("discount");
    if (stored.version < 4) recognized.add("actions");
    if (stored.version < 5) recognized.add("promo");
    if (stored.version < 6) recognized.add("dates");
    return recognized;
  } catch {
    return defaultOptionalColumns();
  }
};
const writeVisibleColumns = (columns: Set<OptionalColumn>) => localStorage.setItem(
  columnPreferenceKey, JSON.stringify({ version: 6, columns: optionalColumns.map(([key]) => key).filter(key => columns.has(key)) })
);

type Props = {
  items: IQuoteLineItem[];
  onChange: (items: IQuoteLineItem[]) => void;
  quoteType: "customer" | "prospect";
  // Issue 35: passed for the background promotion check (verifyPromotionEligibility runs only
  // for a customer quote). null for a prospect.
  customerGuid?: string | null;
  editable?: boolean;
  allowedCustomerSegments?: QuoteSegment[];
  productRows?: IQuoteProduct[];
  productsLoading?: boolean;
  productsError?: boolean;
  productsErrorMessage?: string;
  onAdd?: () => void;
  discounts?: QuoteDiscounts;
  toolbarContent?: ReactNode;
  // Issue 33: the active section's dates. Lines that inherit them (or a line's own override) are
  // shown prorated on screen while editing; the server reprices on save.
  sectionDates?: { startDate: string; endDate: string; endDateSource: "" | "manual" | "coterm" };
};

type ProductCellProps = {
  value: QuoteProductChoice | null;
  choices: QuoteProductChoice[];
  disabled: boolean;
  onChange: (choice: QuoteProductChoice | string | null) => void;
};

type CurrencyInputProps = {
  value: number | null | undefined;
  ariaLabel: string;
  onValueChange: (raw: string) => void;
};

const CurrencyInput = ({ value, ariaLabel, onValueChange }: CurrencyInputProps) => {
  const formatted = value == null ? "" : Number(value).toFixed(2);
  const [draft, setDraft] = useState(formatted);
  const [editing, setEditing] = useState(false);
  useEffect(() => {
    if (!editing) setDraft(formatted);
  }, [editing, formatted]);
  return <TextField type="text" size="small" value={draft}
    inputProps={{ inputMode: "decimal", "aria-label": ariaLabel }}
    onFocus={event => {
      setEditing(true);
      setDraft(value == null ? "" : String(value));
      event.currentTarget.select();
    }}
    onChange={event => {
      setDraft(event.target.value);
      onValueChange(event.target.value);
    }}
    onBlur={() => {
      setEditing(false);
      setDraft(formatted);
    }} />;
};

// The `compact` variant (the line-item Promo column) shows a % sign in place of spin arrows,
// which the field is too narrow for anyway - the Discount column keeps the plain spin-arrow
// version, since that one benefits from small increment/decrement clicks.
const PercentInput = ({ value, ariaLabel, onValueChange, compact }: CurrencyInputProps & { compact?: boolean }) => {
  const formatted = value == null ? "" : String(value);
  const [draft, setDraft] = useState(formatted);
  const [editing, setEditing] = useState(false);
  useEffect(() => {
    if (!editing) setDraft(formatted);
  }, [editing, formatted]);
  return <TextField type="number" size="small" value={draft}
    className={compact ? "percent-input-compact" : undefined}
    // pointerEvents: "none" - purely decorative (no click/select target of its own), so a click
    // or drag-select that lands on the "%" itself passes straight through to the field beneath it
    // instead of selecting the adornment's text. Without this, that selection can leave the
    // actual <input> never focused, so typing afterward silently does nothing.
    InputProps={compact ? { endAdornment: <InputAdornment position="end" sx={{ pointerEvents: "none", userSelect: "none" }}>%</InputAdornment> } : undefined}
    inputProps={{ min: 0, max: 100, step: 0.01, "aria-label": ariaLabel }}
    onFocus={event => { setEditing(true); event.currentTarget.select(); }}
    onChange={event => {
      setDraft(event.target.value);
      onValueChange(event.target.value);
    }}
    onBlur={() => {
      setEditing(false);
      setDraft(formatted);
    }} />;
};

// Issue 35: fallback entry for a promotion the Partner Center feed does not surface (or when
// Partner Center is unreachable). Staff key the Microsoft percentage, the customer promo, the end
// date, and the seat band by hand.
type ManualPromo = { microsoft_promo_pct: number; customer_promo_pct: number; promo_end_date: string; promo_min_quantity: number | null; promo_max_quantity: number | null };
const ManualPromoDialog = ({ open, onClose, onSubmit }: { open: boolean; onClose: () => void; onSubmit: (promo: ManualPromo) => void }) => {
  const [ms, setMs] = useState("");
  const [pass, setPass] = useState("");
  const [end, setEnd] = useState("");
  const [min, setMin] = useState("");
  const [max, setMax] = useState("");
  useEffect(() => { if (open) { setMs(""); setPass(""); setEnd(""); setMin(""); setMax(""); } }, [open]);
  const msNum = Number(ms), passNum = Number(pass);
  const valid = Number.isFinite(msNum) && msNum > 0 && msNum <= 100
    && Number.isFinite(passNum) && passNum >= 0 && passNum <= msNum && end !== ""
    && (min === "" || Number(min) >= 1) && (max === "" || Number(max) >= 1)
    && (min === "" || max === "" || Number(max) >= Number(min));
  return <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
    <DialogTitle>Add a manual promotion</DialogTitle>
    <DialogContent dividers>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
        <TextField label="Microsoft discount %" type="number" size="small" value={ms} onChange={e => setMs(e.target.value)}
          inputProps={{ min: 0, max: 100, step: 0.01 }} />
        <TextField label="Customer promo %" type="number" size="small" value={pass} onChange={e => setPass(e.target.value)}
          inputProps={{ min: 0, max: 100, step: 0.01 }} helperText={`0 to ${ms || "the Microsoft %"}`} />
        <TextField label="Promotion end date" type="date" size="small" value={end} onChange={e => setEnd(e.target.value)}
          InputLabelProps={{ shrink: true }} />
        <Box sx={{ display: "flex", gap: 2 }}>
          <TextField label="Min seats" type="number" size="small" value={min} onChange={e => setMin(e.target.value)} inputProps={{ min: 1, step: 1 }} />
          <TextField label="Max seats" type="number" size="small" value={max} onChange={e => setMax(e.target.value)} inputProps={{ min: 1, step: 1 }} />
        </Box>
      </Box>
    </DialogContent>
    <DialogActions>
      <Button variant="outlined" onClick={onClose}>Cancel</Button>
      <Button variant="contained" disabled={!valid} onClick={() => onSubmit({
        microsoft_promo_pct: msNum, customer_promo_pct: passNum, promo_end_date: end,
        promo_min_quantity: min === "" ? null : Number(min), promo_max_quantity: max === "" ? null : Number(max),
      })}>Add promotion</Button>
    </DialogActions>
  </Dialog>;
};

const ProductCell = ({ value, choices, disabled, onChange }: ProductCellProps) => {
  const [input, setInput] = useState("");
  const [debouncedInput, setDebouncedInput] = useState("");
  useEffect(() => {
    const timer = window.setTimeout(() => setDebouncedInput(input), 250);
    return () => window.clearTimeout(timer);
  }, [input]);
  const filtered = useMemo(() => filterQuoteProducts(choices, debouncedInput), [choices, debouncedInput]);
  return <Autocomplete
    freeSolo
    autoSelect
    forcePopupIcon={false}
    disabled={disabled}
    value={value}
    options={filtered}
    filterOptions={options => options}
    isOptionEqualToValue={(a, b) => typeof b !== "string" && a.product_id === b.product_id && a.sku_id === b.sku_id}
    getOptionLabel={option => typeof option === "string" ? option : productChoiceLabel(option)}
    onInputChange={(_, next) => setInput(next)}
    onChange={(_, next) => {
      if (typeof next !== "string") return onChange(next);
      const normalized = next.trim().toLocaleLowerCase();
      const catalogChoice = choices.find(choice => productChoiceLabel(choice).toLocaleLowerCase() === normalized);
      onChange(catalogChoice || next);
    }}
    renderInput={params => <TextField {...params} label="Type to filter" size="small" />}
  />;
};

const QuoteLineItemGrid = ({ items, onChange, quoteType, customerGuid = null, editable = true, allowedCustomerSegments = [], productRows, productsLoading = false, productsError = false, productsErrorMessage, onAdd, discounts = { P1M: 0, P1YM: 0, P1YA: 0 }, toolbarContent, sectionDates }: Props) => {
  const products = useGetQuoteProducts({ enabled: productRows === undefined });
  const rows = useMemo(() => productRows === undefined ? (products.data || []) : productRows,
    [productRows, products.data]);
  const productCatalogLoading = productsLoading || (productRows === undefined && (products.isLoading || products.isFetching));
  const segments = quoteType === "prospect" ? allSegments
    : allowedCustomerSegments.length ? allowedCustomerSegments : ["Commercial" as QuoteSegment];
  const showActions = true;
  // Drag-to-reorder starts off (not just visually hidden - dragging itself is disabled) so a
  // mobile user brushing the first column can't accidentally pick up and drop a line item. "Move"
  // toggles both on together; it's plain session state, not a stored preference, so it's never
  // stuck on from a previous visit.
  const [moveMode, setMoveMode] = useState(false);
  const showDragColumn = editable && moveMode;
  const columnCount = 13 + (showDragColumn ? 1 : 0);
  const [visibleColumns, setVisibleColumns] = useState<Set<OptionalColumn>>(readVisibleColumns);
  const [columnMenuAnchor, setColumnMenuAnchor] = useState<HTMLElement | null>(null);
  const [openNotes, setOpenNotes] = useState<Set<string | number>>(new Set());
  const [detailIndex, setDetailIndex] = useState<number | null>(null);
  const [dateOverrideIndex, setDateOverrideIndex] = useState<number | null>(null);
  const [dragIndex, setDragIndex] = useState<number | null>(null);
  // Issue 42: the three per-row Actions buttons collapse to one overflow menu, anchored to the
  // row that opened it.
  const [actionMenu, setActionMenu] = useState<{ index: number; anchor: HTMLElement } | null>(null);
  // The line whose Prerequisites dialog is open.
  const [prerequisiteIndex, setPrerequisiteIndex] = useState<number | null>(null);
  const [promotionsCatalogOpen, setPromotionsCatalogOpen] = useState(false);
  const [promoSelectError, setPromoSelectError] = useState<string | null>(null);
  const toggleNote = (key: string | number) => setOpenNotes(current => {
    const next = new Set(current);
    next.has(key) ? next.delete(key) : next.add(key);
    return next;
  });
  const update = (index: number, changes: Partial<IQuoteLineItem>) => onChange(items.map((item, itemIndex) =>
    itemIndex === index ? { ...item, ...changes } : item));
  const move = (from: number, to: number) => {
    if (from === to) return;
    const reordered = [...items];
    const [line] = reordered.splice(from, 1);
    reordered.splice(to, 0, line);
    onChange(reordered.map((item, index) => ({ ...item, sort_order: index + 1 })));
  };

  const priced = (productId: string, skuId: string, segment: string, term: string, quantity: number) => {
    const row = rows.find(candidate => candidate.product_id === productId && candidate.sku_id === skuId &&
      candidate.segment === segment && quoteProductTerm(candidate.term_duration, candidate.billing_plan) === term);
    if (row?.sale_unit_price == null) return {};
    const pricedLine = calculateQuoteLinePricing(row.sale_unit_price, row.cost_unit_price, quantity);
    return { msrp_unit_price: row.msrp_unit_price ?? null,
      sale_unit_price: pricedLine.sale, total_price: pricedLine.total,
		cost_unit_price: row.cost_unit_price ?? null, is_price_manual: false, is_price_unavailable: false,
		price_unavailable_reason: null, margin: pricedLine.margin };
  };

  // Issue 48 follow-up: picking a promotion from the Current Promotions Viewer either applies its
  // customer promo cap to a line that already matches (product/SKU/segment/term), or appends a new
  // line for it, priced the same way choosing the product directly would be. Either way, the
  // Microsoft-side benefit is attached but 0% is shared with the customer by default - staff have
  // to deliberately choose to pass any of it through, not the other way around.
  const applyPromotionRow = (row: PromotionCatalogRow) => {
    // The unconditional enforcement - allowedSegments above only narrows what the dialog itself
    // lets staff browse to/pick, which still leaves a gap for a customer eligible for more than
    // one segment ("All Segments" there still queries every real Microsoft segment, since the
    // catalog API takes one segment at a time, not a list). This check is what actually stops an
    // ineligible-segment promotion from ever being added or attached, regardless of that gap.
    if (!segments.includes(row.segment)) {
      setPromoSelectError(`This customer is not eligible for the ${row.segment} segment.`);
      return;
    }
    const promoFields: Partial<IQuoteLineItem> = {
      promo_source: "partner_center", promo_id: row.promo_id, microsoft_promo_pct: row.microsoft_promo_pct,
      customer_promo_pct: 0, promo_end_date: row.end_date,
      promo_min_quantity: row.min_quantity, promo_max_quantity: row.max_quantity,
      promo_eligibility: null, promo_eligibility_detail: null,
      // The picker knows the name; the prerequisite assessment comes from the next check/save.
      promo_name: row.promotion_name ?? null, promo_prerequisite_skus: null, promo_prerequisite_status: null,
      // A deliberate opt-in through the picker overrides an earlier decline on this line.
      promo_rejected: false,
    };
    const matchIndex = items.findIndex(item => item.product_id === row.product_id && item.sku_id === row.sku_id
      && item.segment === row.segment && item.term_duration === row.term_duration);
    if (matchIndex >= 0) { update(matchIndex, promoFields); setPromotionsCatalogOpen(false); return; }
    onChange([...items, buildCatalogLine({
      product_id: row.product_id, sku_id: row.sku_id, sku_title: row.sku_title || row.product_title || row.product_id,
      segment: row.segment, term_duration: row.term_duration, quantity: Math.max(1, row.min_quantity ?? 1),
    }, promoFields)]);
    setPromotionsCatalogOpen(false);
  };

  // A new automatically-priced line for a catalog product - shared by the promotion picker above
  // and the Prerequisites dialog below, so the two can never drift apart.
  //
  // The catalog this quote's own pricing draws from (`rows`) may not have this exact
  // product/SKU/segment/term - e.g. a promotion whose product isn't in the locally-synced
  // catalog. `priced()` returns {} in that case.
  //
  // A prospect quote never prices any line client-side at all (see the Refresh Prices flow /
  // usePreviewQuotePrices) - `rows` for a prospect carries no prices by design, so `priced()`
  // returning {} here is the ordinary case, exactly like it is for every other way a prospect
  // line gets added (selectProduct always leaves is_price_unavailable false, the same as this
  // needs to). Flagging it unavailable would wrongly short-circuit straight to "enter MSRP and
  // Cost manually" instead of leaving the line for the page's own automatic repricing effect,
  // which is what actually prices it a moment later - matching every other prospect line was
  // the actual fix here, not a pricing lookup this line was missing.
  //
  // A customer quote's client catalog IS authoritative and final (nothing reprices it after),
  // so there `priced()` failing really does mean unpriceable - is_price_unavailable there makes
  // the line immediately actionable (manual MSRP/Cost entry) instead of silently sitting blank.
  const buildCatalogLine = (product: Pick<IQuoteLineItem, "product_id" | "sku_id" | "sku_title" | "segment" | "term_duration" | "quantity">,
    extra: Partial<IQuoteLineItem> = {}): IQuoteLineItem => {
    const pricedFields = priced(product.product_id, product.sku_id, product.segment, product.term_duration, product.quantity);
    return {
      ...product, renewal_date: null,
      msrp_unit_price: null, sale_unit_price: null, total_price: null, is_price_manual: false,
      is_price_unavailable: quoteType === "customer" && pricedFields.sale_unit_price === undefined,
      price_unavailable_reason: null, note: null, inherits_section_dates: true,
      ...extra,
      ...pricedFields,
    };
  };

  // Prerequisites: the catalog products (in this line's own segment - a promotion's list mixes
  // segments, and some of its SKUs aren't in our catalog at all) that would satisfy the line's
  // promotion prerequisite. Partner Center accepts owning any ONE of them, and one on this same
  // quote counts too. Empty once one is already on the quote - checked here too, not only via
  // prerequisite_status, so the button disappears the moment one is added rather than after the
  // next debounced check.
  const prerequisiteChoices = (index: number, descriptor: QuoteLinePromotion | undefined): QuoteProductChoice[] => {
    const item = items[index];
    if (!item || descriptor?.prerequisite_status !== "missing" || !descriptor.prerequisite_skus?.length) return [];
    const keys = new Set(descriptor.prerequisite_skus.map(promotionBigIdKey));
    if (items.some((other, otherIndex) => otherIndex !== index && keys.has(promotionSkuKey(other.product_id, other.sku_id)))) return [];
    return quoteProductsForSegment(rows, item.segment).filter(choice => keys.has(promotionSkuKey(choice.product_id, choice.sku_id)));
  };

  // Details' "Prerequisite" row: every product the promotion accepts (by catalog title, any
  // segment - Partner Center's list is not per segment), and where the line stands on it. Takes
  // the live check's values, or a saved line's stored ones (migration 029).
  const prerequisiteSummary = (skus: string[], status: PromotionPrerequisiteStatus | null | undefined) => {
    const keys = new Set(skus.map(promotionBigIdKey));
    const titles = new Map<string, string>();
    rows.forEach(row => {
      const key = promotionSkuKey(row.product_id, row.sku_id);
      if (keys.has(key) && !titles.has(key)) titles.set(key, row.sku_title);
    });
    const names = Array.from(new Set(Array.from(titles.values())));
    const unknown = keys.size - titles.size;
    const list = `One of: ${names.length ? names.join(", ") : "products not in the catalog"}${names.length && unknown > 0 ? ` (and ${unknown} more not in the catalog)` : ""}`;
    const state = status === "included" ? "one is included on this quote"
      : status === "missing" ? "none included on this quote"
      : status === "owned" ? "customer already owns one" : "";
    return state ? `${list} - ${state}` : list;
  };

  // Appended, never inserted above the promotion line: promoDescriptors is keyed by line index, so
  // shifting the lines below would pair them with the wrong descriptor until the next check.
  // Same segment and quantity as the promotion line (prerequisites are typically per seat), and
  // its term when this product offers it.
  const prerequisiteTerm = (item: IQuoteLineItem, choice: QuoteProductChoice) =>
    item.term_duration && choice.terms.includes(item.term_duration) ? item.term_duration : (choice.terms[0] || "");
  const addPrerequisite = (index: number, choice: QuoteProductChoice) => {
    const item = items[index];
    const term = prerequisiteTerm(item, choice);
    onChange([...items, buildCatalogLine({
      product_id: choice.product_id, sku_id: choice.sku_id, sku_title: choice.sku_title,
      segment: item.segment, term_duration: term, quantity: item.quantity,
    })]);
  };

  const selectProduct = (index: number, choice: QuoteProductChoice | string | null) => {
    if (!choice) return update(index, { product_id: "", sku_id: "", sku_title: "", term_duration: "",
      msrp_unit_price: null, sale_unit_price: null, total_price: null, cost_unit_price: null, margin: null, is_price_manual: false, requested_discount_pct: null });
    if (typeof choice === "string") {
      const title = choice.trim().slice(0, 200);
      if (!title) return;
      const term = items[index].term_duration || "P1M";
      // A custom line is full manual (Issue 36): default its per-line Discount to 0 so the user
      // only has to change it when it isn't zero.
      return update(index, { product_id: `custom:${title}`, sku_id: `custom:${title}`, sku_title: title,
        term_duration: term, msrp_unit_price: null, sale_unit_price: null, total_price: null, cost_unit_price: null, margin: null, is_price_manual: true,
        requested_discount_pct: items[index].requested_discount_pct ?? 0, is_price_unavailable: false, price_unavailable_reason: null });
    }
    const currentTerm = items[index].term_duration;
    const term = currentTerm && choice.terms.includes(currentTerm) ? currentTerm : (choice.terms[0] || "");
    update(index, {
      product_id: choice.product_id, sku_id: choice.sku_id, sku_title: choice.sku_title,
      term_duration: term,
      msrp_unit_price: null, sale_unit_price: null, total_price: null, cost_unit_price: null, margin: null, is_price_manual: false, requested_discount_pct: null,
      is_price_unavailable: false, price_unavailable_reason: null,
      ...priced(choice.product_id, choice.sku_id, items[index].segment, term, items[index].quantity),
    });
  };

  // Issue 36: any edit to a manual line's MSRP, Cost, or Discount re-derives Sale/Total/Margin
  // locally and drops the server's computed adjustment metadata until the next save reprices it.
  const manualUpdate = (index: number, changes: Partial<IQuoteLineItem>) => {
    const current = items[index];
    const merged: IQuoteLineItem = {
      ...current, ...changes, is_price_manual: true, is_price_unavailable: false, price_unavailable_reason: null,
      applied_discount_pct: undefined, adjustment_reason: undefined, is_commission_eligible: undefined,
    };
    // Backfill a missing per-line Discount (a line that predates Issue 36 / migration 019): derive
    // it from any existing MSRP and Sale, otherwise start at 0. The user can still clear the field.
    if (merged.requested_discount_pct == null && !("requested_discount_pct" in changes)) {
      merged.requested_discount_pct = current.msrp_unit_price != null && current.msrp_unit_price > 0 && current.sale_unit_price != null
        ? Math.min(100, Math.max(0, Math.round((current.msrp_unit_price - current.sale_unit_price) / current.msrp_unit_price * 10000) / 100))
        : 0;
    }
    update(index, calculateManualLinePricing(merged));
  };

  const manualPrice = (index: number, raw: string) => {
    const value = raw === "" ? null : Number(raw);
    if (value != null && (!Number.isFinite(value) || value < 0)) return;
    manualUpdate(index, { manual_msrp_unit_price: value });
  };

  const manualCost = (index: number, raw: string) => {
    if (raw === "") return manualUpdate(index, { manual_cost_unit_price: null });
    const cost = Number(raw);
    if (!Number.isFinite(cost) || cost < 0) return;
    manualUpdate(index, { manual_cost_unit_price: cost });
  };

  const manualDiscount = (index: number, raw: string) => {
    if (raw === "") return manualUpdate(index, { requested_discount_pct: null });
    const pct = Number(raw);
    if (!Number.isFinite(pct) || pct < 0 || pct > 100) return;
    manualUpdate(index, { requested_discount_pct: pct });
  };

  // Issue 35: the background promotion check. Fires (debounced) when a line's promotion-relevant
  // fields settle. Descriptors are held here, keyed by line index; the line item only ever
  // carries promo_source + customer_promo_pct (set when staff enter a customer promo) plus, on a
  // saved quote, the server's snapshot. Custom / manual / incomplete lines are not checked.
  const promoCheck = useQuoteLinePromotions();
  const [promoDescriptors, setPromoDescriptors] = useState<Record<number, QuoteLinePromotion>>({});
  const [promoSyncOk, setPromoSyncOk] = useState(true);
  const [manualPromoIndex, setManualPromoIndex] = useState<number | null>(null);
  const isCheckableLine = (item: IQuoteLineItem) => Boolean(
    editable && item.product_id && item.sku_id && !item.product_id.startsWith("custom:") && !item.is_price_manual
    && (["P1M", "P1YM", "P1YA"] as string[]).includes(item.term_duration)
    && (["Commercial", "Charity", "Education", "Government"] as string[]).includes(item.segment)
    && item.quantity > 0);
  const promoSignature = useMemo(() => JSON.stringify(items.map(item =>
    isCheckableLine(item) ? [item.product_id, item.sku_id, item.term_duration, item.segment, item.quantity] : null)) + `|${customerGuid ?? ""}`,
    [items, customerGuid, editable]); // eslint-disable-line react-hooks/exhaustive-deps
  const [debouncedSignature, setDebouncedSignature] = useState(promoSignature);
  useEffect(() => {
    const timer = window.setTimeout(() => setDebouncedSignature(promoSignature), 500);
    return () => window.clearTimeout(timer);
  }, [promoSignature]);
  useEffect(() => {
    const entries = items.map((item, index) => ({ item, index })).filter(({ item }) => isCheckableLine(item));
    if (entries.length === 0) { setPromoDescriptors({}); return; }
    let cancelled = false;
    promoCheck.mutateAsync({
      customer_guid: customerGuid ?? null,
      lines: entries.map(({ item }) => ({
        product_id: item.product_id, sku_id: item.sku_id,
        term_duration: item.term_duration as QuoteTerm, segment: item.segment as QuoteSegment, quantity: item.quantity,
      })),
    }).then(result => {
      if (cancelled) return;
      setPromoSyncOk(result.sync_ok);
      const map: Record<number, QuoteLinePromotion> = {};
      entries.forEach(({ index }, i) => { if (result.lines[i]) map[index] = result.lines[i]; });
      setPromoDescriptors(map);
    }).catch(() => { if (!cancelled) { setPromoSyncOk(false); setPromoDescriptors({}); } });
    return () => { cancelled = true; };
  }, [debouncedSignature]); // eslint-disable-line react-hooks/exhaustive-deps

  const clearPromo = (item: IQuoteLineItem): IQuoteLineItem => ({
    ...item, promo_source: null, promo_id: null, microsoft_promo_pct: null, customer_promo_pct: null,
    promo_end_date: null, promo_min_quantity: null, promo_max_quantity: null, promo_eligibility: null, promo_eligibility_detail: null,
    promo_name: null, promo_prerequisite_skus: null, promo_prerequisite_status: null,
  });

  // Issue 33: line-level date override <-> the modal's EditorSectionDates shape.
  const lineDatesValue = (item: IQuoteLineItem) => item.inherits_section_dates === false ? {
    ...emptySectionDates(),
    startDate: item.start_date || "",
    endDate: item.end_date || "",
    endDateSource: (item.end_date_source || "") as "" | "manual" | "coterm",
    cotermTargetIds: item.coterm_target_subscription_id ? JSON.stringify([item.coterm_target_subscription_id]) : null,
    cotermTargetLabel: item.coterm_target_label ?? null,
  } : emptySectionDates();

  // `extra` merges into the SAME update() call as the dates fields (e.g. a renewal date from the
  // same confirm) - two separate update() calls in one handler would each read the same
  // pre-update `items` closure and the second would silently discard the first's change.
  const applyLineDates = (index: number, dates: ReturnType<typeof emptySectionDates>, inherits: boolean, extra: Partial<IQuoteLineItem> = {}) => {
    if (inherits) return update(index, {
      inherits_section_dates: true, start_date: null, end_date: null, end_date_source: null,
      coterm_target_subscription_id: null, coterm_target_label: null, ...extra,
    });
    let firstId: string | null = null;
    try { firstId = (JSON.parse(dates.cotermTargetIds || "null") as string[] | null)?.[0] ?? null; } catch { firstId = null; }
    update(index, {
      inherits_section_dates: false, start_date: dates.startDate, end_date: dates.endDate || null,
      end_date_source: dates.endDateSource || null,
      coterm_target_subscription_id: dates.endDateSource === "coterm" ? firstId : null,
      coterm_target_label: dates.endDateSource === "coterm" ? dates.cotermTargetLabel : null,
      ...extra,
    });
  };

  const lineDatesLabel = (item: IQuoteLineItem): string => {
    if (item.inherits_section_dates === false)
      return item.start_date ? `${formatIsoDate(item.start_date)} - ${formatIsoDate(item.end_date || item.charge_end_date)}` : "Custom dates";
    // Issue 50 (50.5): billable_days < full_period_days replaces the dropped proration_factor as
    // the "is this line actually prorated" gate.
    return item.charge_start_date && item.billable_days != null && item.full_period_days != null && item.billable_days < item.full_period_days
      ? `${formatIsoDate(item.charge_start_date)} - ${formatIsoDate(item.charge_end_date)}`
      : "";
  };

  // A partner_center promotion that is no longer offered (or whose line stopped matching one):
  // drop it so the line reverts to non-promotional pricing. Conversely, a newly discovered
  // promotion is attached immediately, at a 0% customer promo by default - its Microsoft-side
  // cost benefit (and therefore margin) applies right away regardless of whether staff choose to
  // share any of it with the customer; the Promo column is only about how much gets shared.
  // Never for a "requirements_unmet" match, though - the server would zero its benefit right back
  // out on save anyway (see ResolveLinePromo), but auto-attaching it here first would still
  // momentarily show a discounted price for a customer the system itself isn't confident is
  // eligible. promoStatus below tells staff the promotion exists and why it wasn't applied
  // instead; a manual promo entry is the deliberate override for a case staff have confirmed
  // themselves. Nor for a line staff explicitly rejected (item.promo_rejected, persisted on the
  // line itself so the decision survives a save and reload) - without that check this would just
  // re-offer the same promotion the instant it's removed.
  // Shared shape for attaching a partner_center match to a line - used both by the auto-attach
  // effect below and by Accept (staff opting back in after a decline), so the two can never
  // drift apart.
  const attachPromoFields = (descriptor: QuoteLinePromotion): Partial<IQuoteLineItem> => ({
    promo_source: "partner_center", customer_promo_pct: 0,
    promo_id: descriptor.promo_id ?? null, microsoft_promo_pct: descriptor.microsoft_promo_pct ?? null,
    promo_end_date: descriptor.promo_end_date ?? null, promo_min_quantity: descriptor.promo_min_quantity ?? null,
    promo_max_quantity: descriptor.promo_max_quantity ?? null, promo_eligibility: descriptor.eligibility ?? null,
    promo_eligibility_detail: descriptor.eligibility_detail ?? null, promo_rejected: false,
    promo_name: descriptor.promotion_name ?? null,
    promo_prerequisite_skus: descriptor.prerequisite_skus?.length ? descriptor.prerequisite_skus : null,
    promo_prerequisite_status: descriptor.prerequisite_skus?.length ? descriptor.prerequisite_status ?? null : null,
  });

  useEffect(() => {
    const next = items.map((item, index) => {
      const descriptor = promoDescriptors[index];
      if (item.promo_source === "partner_center") {
        if (!isCheckableLine(item) || !descriptor) return item;
        if (!descriptor.has_promotion) return clearPromo(item);
        // Prerequisites: a promotion that applied only because its prerequisite was included on
        // this quote (the server reports that as "met") comes back "requirements_unmet"
        // once that prerequisite line is removed - drop it rather than keep a discount the
        // customer no longer qualifies for. Other failures (a seat band, first purchase) are left
        // as they were.
        return descriptor.eligibility === "requirements_unmet" && descriptor.prerequisite_status === "missing" ? clearPromo(item) : item;
      }
      if (!item.promo_source && descriptor?.has_promotion && descriptor.eligibility !== "requirements_unmet"
          && !item.promo_rejected && isCheckableLine(item)) {
        return { ...item, ...attachPromoFields(descriptor) };
      }
      return item;
    });
    if (next.some((item, index) => item !== items[index])) onChange(next);
  }, [promoDescriptors]); // eslint-disable-line react-hooks/exhaustive-deps

  // Reversing a decline (the "Accept" button): if the promotion is still there and still
  // eligible, re-attach it right away (matching what the auto-attach effect would do on its own
  // next tick, just not delayed until then); otherwise just clear the flag so the line goes back
  // to whatever its natural status is (nothing, or "not applied" if it's since become ineligible).
  const acceptPromo = (index: number, descriptor: QuoteLinePromotion | undefined) => {
    update(index, descriptor?.has_promotion && descriptor.eligibility !== "requirements_unmet"
      ? attachPromoFields(descriptor) : { promo_rejected: false });
  };

  // Same rule as promoStatus above: a manual entry's own Microsoft percent is the cap, never a
  // competing automatic descriptor's - otherwise sharing more (or less) than staff actually
  // entered could sneak in through whichever number the automatic check happens to report for the
  // same line.
  const promoCap = (item: IQuoteLineItem, descriptor?: QuoteLinePromotion) =>
    (item.promo_source === "manual" ? item.microsoft_promo_pct : (descriptor?.microsoft_promo_pct ?? item.microsoft_promo_pct)) ?? 100;

  const setPassThrough = (index: number, raw: string, descriptor?: QuoteLinePromotion) => {
    const item = items[index];
    // Clearing/zeroing this field only zeroes what's SHARED with the customer - it must never
    // detach the promotion itself (clearPromo), which would also drop its Microsoft-side
    // cost/margin benefit. A promotion, once available, stays applied regardless of whether any
    // of it is shared; this field is only ever about the share amount.
    if (raw === "" || Number(raw) === 0) return update(index, { customer_promo_pct: 0 });
    const value = Number(raw);
    if (!Number.isFinite(value) || value < 0) return;
    const capped = Math.min(value, promoCap(item, descriptor));
    update(index, item.promo_source === "manual"
      ? { customer_promo_pct: capped }
      : {
        promo_source: "partner_center", customer_promo_pct: capped,
        promo_id: descriptor?.promo_id ?? item.promo_id ?? null,
        microsoft_promo_pct: descriptor?.microsoft_promo_pct ?? item.microsoft_promo_pct ?? null,
        promo_end_date: descriptor?.promo_end_date ?? item.promo_end_date ?? null,
        promo_min_quantity: descriptor?.promo_min_quantity ?? item.promo_min_quantity ?? null,
        promo_max_quantity: descriptor?.promo_max_quantity ?? item.promo_max_quantity ?? null,
        promo_eligibility: descriptor?.eligibility ?? item.promo_eligibility ?? null,
        promo_eligibility_detail: descriptor?.eligibility_detail ?? item.promo_eligibility_detail ?? null,
        promo_name: descriptor?.promotion_name ?? item.promo_name ?? null,
        // Typing a share percent directly is itself an opt-in - overrides an earlier decline.
        promo_rejected: false,
      });
  };

  // A promotion is attached (its Microsoft-side cost/margin benefit already applies) the moment
  // it's discovered or picked - see the auto-attach effect above - independent of whether any of
  // it is shared with the customer (customer_promo_pct, which starts at 0, and can be blanked
  // back to 0 without ever detaching the promotion - see setPassThrough). The message just states
  // that plainly (with the Microsoft percent, e.g. "25% promotion applied") - explaining how to
  // change the share amount belongs in the Promo column itself, not repeated in a banner under
  // every promoted line.
  const promoStatus = (item: IQuoteLineItem, descriptor: QuoteLinePromotion | undefined, checkable: boolean): string | null => {
    if (item.promo_source) {
      // A manual entry is staff's own deliberate, explicit override - it must never be shadowed by
      // whatever the automatic Partner Center check happens to also report for the same line (a
      // different promotion, quite possibly with its own percent/eligibility). Without this check,
      // ?? preferring the descriptor meant adding a manual promotion "on top of" an automatic one
      // the system still found kept showing the automatic one's numbers, not what was actually
      // attached - descriptor values only apply to a "partner_center" line, where they're the
      // SAME promotion's current live state, not a competing one.
      const manual = item.promo_source === "manual";
      const state = manual ? item.promo_eligibility : (descriptor?.eligibility ?? item.promo_eligibility);
      const detail = manual ? item.promo_eligibility_detail : (descriptor?.eligibility_detail ?? item.promo_eligibility_detail);
      // Why it isn't confirmed matters more than the bare fact (e.g. a per-customer redemption
      // limit nothing can check for a prospect), so both states say it. A "met" line's note (e.g.
      // "prerequisite included on this quote") is in Details only.
      const reason = eligibilityReason(detail);
      const eligibilityNote = state === "requirements_unmet" ? ` - requirements not met${reason ? `: ${reason}` : ""}`
        : state === "unverified" ? ` - eligibility not verified${reason ? `: ${reason}` : ""}` : "";
      const pct = manual ? item.microsoft_promo_pct : (descriptor?.microsoft_promo_pct ?? item.microsoft_promo_pct);
      return `${pct != null ? `${pct}% promotion` : "Promotion"} applied${eligibilityNote}`;
    }
    // Staff turned this down themselves - said before anything else below, since it's a
    // deliberate decision and shouldn't read the same as "never looked at" (has_promotion, no
    // message) or "the system won't offer it" (requirements_unmet). Shown regardless of whether
    // the promotion the check currently reports even still exists, so the Accept control
    // always has somewhere to live.
    if (item.promo_rejected) return "Promotion declined";
    // A match exists but the auto-attach effect above deliberately skipped it because the server
    // itself isn't confident this customer qualifies - see ResolveLinePromo, which would zero the
    // benefit right back out on save anyway. Say so plainly instead of silently doing nothing, so
    // staff isn't left wondering why a line that looks promo-eligible in the catalog never priced
    // with a discount.
    if (descriptor?.has_promotion && descriptor.eligibility === "requirements_unmet") {
      return `Promotion not applied - requirements not met${descriptor.eligibility_detail ? `: ${descriptor.eligibility_detail}` : ""}`;
    }
    if (descriptor?.has_promotion) return "Promotion available";
    // Upsell signal: nothing applies to this line as configured, but this exact offer does have
    // a promotion at a different term - worth a look before assuming there's nothing to offer.
    if (descriptor?.upsell_hint) return `A promotion is available for this offer at a different term (${descriptor.upsell_hint}).`;
    if (checkable && !promoSyncOk) return "Could not check for promotions right now.";
    return null;
  };

  const hiddenColumnClasses = optionalColumns.filter(([key]) => !visibleColumns.has(key)).map(([key]) => `hide-${key}`).join(" ");

  return <Box className={`quote-line-item-grid ${hiddenColumnClasses}`}>
    {(products.isError || productsError) && <Alert severity="error">{productsErrorMessage || "Products could not be loaded. Try again, or confirm the customer is active in Partner Center and has eligible NCE products."}</Alert>}
    <Box className="column-visibility-toolbar">
      <Box className="quote-grid-toolbar-start">
        {toolbarContent}
        <Tooltip enterDelay={1800} title="See every currently active Partner Center promotion, starting scoped to this quote's segment.">
          <Button variant="outlined" startIcon={<LocalOfferOutlinedIcon />} onClick={() => { setPromoSelectError(null); setPromotionsCatalogOpen(true); }}>Promos</Button>
        </Tooltip>
      </Box>
      <Box className="quote-grid-toolbar-buttons">
        {editable && <Tooltip enterDelay={1800} title={moveMode ? "Hide the drag handle and lock line order." : "Show a drag handle to reorder line items."}>
          <Button variant={moveMode ? "contained" : "outlined"} startIcon={<DragIndicatorIcon />} aria-label={moveMode ? "Done" : "Move"}
            aria-pressed={moveMode} onClick={() => setMoveMode(current => !current)}>{moveMode ? "Done" : "Move"}</Button>
        </Tooltip>}
        <Tooltip enterDelay={1800} title="Show or hide optional columns.">
          <Button variant="outlined" startIcon={<ViewColumnIcon />} aria-label="Columns" onClick={event => setColumnMenuAnchor(event.currentTarget)} aria-haspopup="true"
            aria-expanded={Boolean(columnMenuAnchor)}>Columns</Button>
        </Tooltip>
      </Box>
      <Menu anchorEl={columnMenuAnchor} open={Boolean(columnMenuAnchor)} onClose={() => setColumnMenuAnchor(null)}>
        {optionalColumns.map(([key, label]) => <MenuItem key={key} onClick={() => setVisibleColumns(current => {
          const next = new Set(current);
          next.has(key) ? next.delete(key) : next.add(key);
          writeVisibleColumns(next);
          return next;
        })}><Checkbox checked={visibleColumns.has(key)} /><ListItemText primary={label} /></MenuItem>)}
      </Menu>
    </Box>
    <TableContainer tabIndex={0} aria-label="Quote line items; scroll horizontally to view additional columns">
      <Table size="small">
        <colgroup>
          {showDragColumn && <col className="drag-column" />}
          <col className="product-column" />
          <col className="segment-column" />
          <col className="term-column" />
          <col className="quantity-column" />
          <col className="msrp-column" />
          <col className="cost-column" />
          <col className="discount-column" />
          <col className="promo-column" />
          <col className="sale-column" />
          <col className="total-column" />
          <col className="margin-column" />
          <col className="dates-column" />
          <col className="action-column" />
        </colgroup>
        <TableHead><TableRow>
          {showDragColumn && <TableCell className="drag-cell" aria-label="Reorder" />}
          <TableCell className="product-cell">Offer</TableCell><TableCell className="segment-cell">Segment</TableCell><TableCell className="term-cell">Term</TableCell>
          <TableCell className="quantity-cell">Quantity</TableCell><TableCell className="unit-price-cell msrp-cell">MSRP</TableCell><TableCell className="unit-price-cell cost-cell">Cost</TableCell>
          <TableCell className="discount-cell">Discount</TableCell><TableCell className="promo-cell">Promo</TableCell><TableCell className="unit-price-cell sale-cell">Sale</TableCell>
          <TableCell className="total-price-cell">Total</TableCell><TableCell className="margin-cell">Margin</TableCell><TableCell className="dates-cell">Dates</TableCell>{showActions && <TableCell className="action-cell">Actions</TableCell>}
        </TableRow></TableHead>
        <TableBody>{items.map((item, index) => {
          const choices = quoteProductsForSegment(rows, item.segment);
          const choice = choices.find(candidate => candidate.product_id === item.product_id && candidate.sku_id === item.sku_id) || (item.product_id && item.sku_id ? {
            product_id: item.product_id, sku_id: item.sku_id, product_title: item.sku_title,
            sku_title: item.sku_title, terms: item.product_id.startsWith("custom:") ? ["P1M", "P1YM", "P1YA"] : item.term_duration ? [item.term_duration] : [],
          } : null);
          const catalogRow = rows.find(row => row.product_id === item.product_id && row.sku_id === item.sku_id &&
            row.segment === item.segment && quoteProductTerm(row.term_duration, row.billing_plan) === item.term_duration);
          const promoDescriptor = promoDescriptors[index];
          const lineCheckable = isCheckableLine(item);
          const isCallingPlanLine = item.adjustment_reason === "product_restriction";
          // Issue 50 (50.1): the grid never recomputes a price the server already computed - every
          // displayed Sale/Total/Margin is exactly what the last Save or live preview response
          // returned, for every line type. displayedCost falls back to the catalog listing only for
          // a line the server hasn't priced yet (a brand-new automatic line before its first
          // preview); once priced, item.cost_unit_price is always the authoritative, already-
          // (correctly, once-)prorated figure.
          const displayedCost = item.cost_unit_price ?? catalogRow?.cost_unit_price ?? null;
          const displayedSale = item.sale_unit_price;
          const displayedTotal = item.total_price;
          const displayedMargin = item.margin ?? null;
          const availableTerms = [...(choice?.terms || [])];
          if (item.term_duration && !availableTerms.includes(item.term_duration)) availableTerms.push(item.term_duration);
          // Issue 50 (50.4): a manual line's completeness is judged on its raw manual_msrp_unit_price/
          // manual_cost_unit_price inputs, not the computed msrp_unit_price/cost_unit_price - those
          // stay null until a preview/save response lands.
          const manualError = !item.is_price_manual ? null
            : item.manual_msrp_unit_price == null || item.manual_cost_unit_price == null ? "Enter MSRP and Cost for manual pricing."
            : item.manual_cost_unit_price > item.manual_msrp_unit_price ? "Cost cannot exceed MSRP."
            : item.requested_discount_pct == null ? "Enter a Discount percentage for manual pricing."
            : null;
          const legacy = item.price_unavailable_reason?.toLowerCase().includes("legacy/non-nce");
          const custom = item.product_id.startsWith("custom:") || item.is_price_manual || item.is_price_unavailable;
          // Only a saved, priced line carries an authoritative reason - never guessed while a
          // manual line is still being edited client-side, and unavailable lines stay unknown.
          const adjusted = !item.is_price_unavailable && Boolean(item.adjustment_reason);
          const adjustmentText = adjusted ? adjustmentMessage(item.adjustment_reason, item.is_price_manual) : null;
          const rowKey = item.quote_line_item_id || `${item.product_id}-${item.sku_id}-${index}`;
          const noteOpen = openNotes.has(rowKey);
          return <Fragment key={rowKey}><TableRow className={[legacy ? "legacy-price-row" : "", item.is_price_unavailable ? "price-unavailable-row" : "", custom ? "custom-price-row" : "", adjusted ? "discount-adjusted-row" : ""].filter(Boolean).join(" ")}>
            {showDragColumn && <TableCell className="drag-cell"><Tooltip enterDelay={1800} title="Drag to reorder this line item."><IconButton size="small" draggable aria-label={`Reorder line ${index + 1}`}
              onDragStart={() => setDragIndex(index)} onDragEnd={() => setDragIndex(null)} onDragOver={event => event.preventDefault()}
              onDrop={() => { if (dragIndex != null) move(dragIndex, index); setDragIndex(null); }}><DragIndicatorIcon fontSize="small" /></IconButton></Tooltip></TableCell>}
            <TableCell className="product-cell"><Box className="product-cell-layout"><ProductCell value={choice} choices={choices} disabled={!editable} onChange={next => selectProduct(index, next)} /></Box></TableCell>
            <TableCell className="segment-cell"><TextField className="quick-quote-select" select size="small" value={item.segment} disabled={!editable} onChange={event => {
              const segment = event.target.value as QuoteSegment;
              // A segment correction (e.g. the customer's real segment wasn't detected correctly
              // on import) keeps the product/SKU/term the same product catalogs are commonly
              // shared across segments for the same customer, only its price differs - and just
              // re-derives pricing for the new segment, the same way the Term handler above
              // re-derives pricing for a new term rather than blanking the whole line.
              if (item.is_price_manual) return update(index, calculateManualLinePricing({ ...item, segment }));
              update(index, { segment, msrp_unit_price: null, sale_unit_price: null, total_price: null, cost_unit_price: null, margin: null,
                is_price_manual: false, requested_discount_pct: null, is_price_unavailable: false, price_unavailable_reason: null,
                ...priced(item.product_id, item.sku_id, segment, item.term_duration, item.quantity) });
            }} inputProps={{ "aria-label": `Segment for line ${index + 1}` }}>
              {segments.map(segment => <MenuItem key={segment} value={segment}>{segment}</MenuItem>)}
            </TextField></TableCell>
            <TableCell className="term-cell"><TextField className="quick-quote-select" select size="small" value={item.term_duration} disabled={!editable || !choice} onChange={event => {
              const term = event.target.value as IQuoteLineItem["term_duration"];
              if (item.is_price_manual) return update(index, calculateManualLinePricing({ ...item, term_duration: term }));
              update(index, { term_duration: term, msrp_unit_price: null, sale_unit_price: null, total_price: null, cost_unit_price: null, margin: null, is_price_manual: false, requested_discount_pct: null,
                ...priced(item.product_id, item.sku_id, item.segment, term, item.quantity) });
            }} inputProps={{ "aria-label": `Term for line ${index + 1}` }}>
              {availableTerms.map(term => <MenuItem key={term} value={term}>{term}</MenuItem>)}
            </TextField></TableCell>
            <TableCell className="quantity-cell"><TextField type="number" size="small" value={Number(item.quantity) || 1} disabled={!editable} inputProps={{ min: 1, step: 1, "aria-label": `Quantity for line ${index + 1}` }} onChange={event => {
              const quantity = Math.max(1, Math.floor(Number(event.target.value) || 1));
              if (item.is_price_manual) return update(index, calculateManualLinePricing({ ...item, quantity }));
			  const total = item.sale_unit_price == null ? null : calculateQuoteLinePricing(item.sale_unit_price, item.cost_unit_price, quantity).total;
              update(index, { quantity, total_price: total });
            }} /></TableCell>
            <TableCell className={`unit-price-cell msrp-cell ${(item.is_price_unavailable || item.is_price_manual) && editable ? "manual-price-cell" : ""}`}>{(item.is_price_unavailable || item.is_price_manual) && editable ? <CurrencyInput value={item.manual_msrp_unit_price} ariaLabel={`Manual MSRP for line ${index + 1}`} onValueChange={raw => manualPrice(index, raw)} /> : <Typography>{unitMoney(item.msrp_unit_price)}</Typography>}</TableCell>
            <TableCell className={`unit-price-cell cost-cell ${(item.is_price_unavailable || item.is_price_manual) && editable ? "manual-price-cell" : ""}`}>{(item.is_price_unavailable || item.is_price_manual) && editable
              ? <CurrencyInput value={item.manual_cost_unit_price} ariaLabel={`Manual cost for line ${index + 1}`} onValueChange={raw => manualCost(index, raw)} />
              : <Typography>{unitMoney(displayedCost)}</Typography>}</TableCell>
            <TableCell className={`discount-cell ${adjusted ? "discount-adjusted-value" : ""} ${(item.is_price_unavailable || item.is_price_manual) && editable ? "manual-price-cell" : ""}`}>{(item.is_price_unavailable || item.is_price_manual) && editable
              ? <PercentInput value={item.requested_discount_pct} ariaLabel={`Manual discount percent for line ${index + 1}`} onValueChange={raw => manualDiscount(index, raw)} />
              : <Typography>{item.applied_discount_pct != null ? `${item.applied_discount_pct}%` : item.term_duration ? `${discounts[item.term_duration]}%` : ""}</Typography>}</TableCell>
            {/* Always a computed value, never a button - "Add Promo" moved to the Actions menu so
                this column only ever needs to be as wide as a percent, or blank. Blank (not a
                "Declined" label or similar) whenever nothing is actually attached - a declined
                line, a requirements_unmet match the system won't offer, an offer with no
                promotion at all - the banner row below already carries that explanation, so the
                column itself stays a plain, empty data cell rather than repeating it.

                Gated on item.promo_source alone, NOT promoDescriptor?.has_promotion - a real
                promotion being reported as existing is not the same as one actually being
                attached to this line (see promoStatus below, which draws exactly this same
                distinction). has_promotion is also true for a requirements_unmet match (one the
                system deliberately never auto-attaches - see the auto-attach effect above) and for
                a declined-then-accepted-but-still-ineligible line, in both of which promo_source
                stays null on purpose. Showing an editable field there let staff type ANY nonzero
                percent to silently attach the promotion anyway (setPassThrough's own fallback path)
                - bypassing that safety rule entirely from what looked like an innocuous share
                field, and confusingly making Decline reappear as a side effect once it did. Typing
                0 into the same field looked like it "didn't work" for the same reason: it only
                calls update({ customer_promo_pct: 0 }), which sets nothing else, so nothing visibly
                changes when nothing is attached yet to begin with. The sanctioned way to attach a
                promotion the system won't offer on its own is the Actions menu's Add Promo, a
                deliberate, harder-to-trigger-by-accident action - never this field. */}
            <TableCell className="promo-cell">{isCallingPlanLine
              ? <Tooltip title="A customer promo is not available for this product."><Typography color="text.secondary">n/a</Typography></Tooltip>
              : item.promo_source
                ? <PercentInput compact value={item.customer_promo_pct} ariaLabel={`Customer promo percent for line ${index + 1}`}
                    onValueChange={raw => editable && setPassThrough(index, raw, promoDescriptor)} />
                : null}</TableCell>
            <TableCell className="unit-price-cell sale-cell"><Typography>{unitMoney(displayedSale)}</Typography></TableCell>
            <TableCell className="total-price-cell"><Typography>{money(displayedTotal)}</Typography></TableCell>
            <TableCell className="margin-cell"><Typography>{displayedMargin == null ? "" : `${displayedMargin.toFixed(2)}%`}</Typography></TableCell>
            {/* One combined Dates column (used to be two: this line's own start/end override, and
                its imported Renewal date) - a full "Sep 11, 2026 - Apr 30, 2027" range never fit
                either column, and a line that just inherits the section's dates with no renewal
                on file has nothing line-specific to show anyway, so the cell stays blank then.
                Rollover for whichever of the two applies in a tooltip; click opens the same
                edit-dates dialog the Actions menu does, which edits both together. */}
            <TableCell className="dates-cell">{(() => {
              const customDates = item.inherits_section_dates === false;
              const hasRenewal = Boolean(item.renewal_date);
              if (!customDates && !hasRenewal && !editable) return null;
              // A section-level manual end date is flagged on the section's dates row instead.
              const firstTermWarning = customDates
                ? pastFirstTermWarning(item.start_date, item.end_date, item.end_date_source, [item.term_duration as QuoteTerm])
                : null;
              const tooltipParts = [customDates ? lineDatesLabel(item) : null, hasRenewal ? `Renewal: ${displayDate(item.renewal_date)}` : null, firstTermWarning].filter(Boolean);
              return <Tooltip title={tooltipParts.length ? tooltipParts.join("; ") : "Set dates or a renewal date"}>
                <IconButton size="small" aria-label={`Dates for line ${index + 1}${tooltipParts.length ? `: ${tooltipParts.join(", ")}` : ""}`}
                  onClick={() => setDateOverrideIndex(index)}>
                  <EventOutlinedIcon fontSize="small" color={firstTermWarning ? "warning" : tooltipParts.length ? "inherit" : "disabled"} />
                </IconButton>
              </Tooltip>;
            })()}</TableCell>
            {showActions && <TableCell className="action-cell">
              <IconButton aria-label={`Actions for line ${index + 1}`} aria-haspopup="true" aria-expanded={actionMenu?.index === index}
                onClick={event => setActionMenu({ index, anchor: event.currentTarget })}><MoreVertIcon /></IconButton>
            </TableCell>}
          </TableRow>{noteOpen && <TableRow className="note-editor-row"><TableCell colSpan={columnCount}>
            <TextField fullWidth multiline minRows={2} size="small" label="Notes" value={item.note || ""} disabled={!editable}
              error={(item.note?.length || 0) > 2000} helperText={`${item.note?.length || 0}/2000`}
              inputProps={{ maxLength: 2000, "aria-label": `Notes for line ${index + 1}` }} onChange={event => {
                if (event.target.value.length <= 2000) update(index, { note: event.target.value });
              }} />
          </TableCell></TableRow>}{(item.is_price_unavailable || manualError) && <TableRow className="line-error-row"><TableCell colSpan={columnCount}>
            <ErrorOutlineIcon fontSize="small" /> <span>{manualError || item.price_unavailable_reason || "A current price is unavailable. Enter MSRP and Cost or remove this line."}</span>
          </TableCell></TableRow>}{adjustmentText && <TableRow className="line-adjustment-row"><TableCell colSpan={columnCount}>
            <TrendingDownIcon fontSize="small" /> <span>{adjustmentText}</span>
          </TableCell></TableRow>}{(() => {
            const status = promoStatus(item, promoDescriptor, lineCheckable);
            const prerequisites = editable ? prerequisiteChoices(index, promoDescriptor) : [];
            return status ? <TableRow className="line-promo-row"><TableCell colSpan={columnCount}>
              <LocalOfferOutlinedIcon fontSize="small" /> <span>{status}</span>
              {(promoDescriptor?.has_promotion || item.promo_source) && <Button size="small" variant="outlined" onClick={() => setDetailIndex(index)}>Details</Button>}
              {/* The promotion requires the customer to own another product - lets staff pick
                  one and add it to this quote instead of having to look up which product that is. */}
              {prerequisites.length > 0 && <Tooltip describeChild enterDelay={1800} title="Choose a product this promotion requires and add it to the quote.">
                <Button size="small" variant="outlined" aria-haspopup="dialog"
                  onClick={() => setPrerequisiteIndex(index)}>Prerequisites</Button>
              </Tooltip>}
              {/* The worst outcome here is offering a promotion a customer turns out not to
                  qualify for - staff need a way to say no even to one the system offered in good
                  faith, and that decision needs to survive a save and reload. Only offered once
                  actually attached; clearPromo also drops its Microsoft-side cost benefit, same
                  as it does everywhere else it's called, and promo_rejected (the field name is
                  unchanged - a persisted database column, not just UI copy - see migration 027;
                  sent to the server on save) stops the auto-attach effect from immediately
                  re-adding it. "Decline"/"Accept" read as the natural pair for staff turning down
                  (or reinstating) a benefit that's already on offer - matches the existing
                  "Promotion declined" status text above for free, unlike "Reject"/"Un-reject",
                  which was never a real word and had no natural opposite. */}
              {editable && item.promo_source && <Button size="small" variant="outlined" onClick={() => {
                update(index, { ...clearPromo(item), promo_rejected: true });
              }}>Decline</Button>}
              {/* A decline is reversible - staff can change their mind without having to
                  rediscover the same promotion through the catalog picker. */}
              {editable && !item.promo_source && item.promo_rejected && <Button size="small" variant="outlined"
                onClick={() => acceptPromo(index, promoDescriptor)}>Accept</Button>}
            </TableCell></TableRow> : null;
          })()}</Fragment>;
        })}</TableBody>
      </Table>
    </TableContainer>
    {/* Issue 42: one overflow menu for the per-row View details / Notes / Remove actions, anchored
        to whichever row's Actions button was last clicked. */}
    <Menu className="line-action-menu" anchorEl={actionMenu?.anchor ?? null} open={actionMenu !== null} onClose={() => setActionMenu(null)}>
      {(() => {
        if (actionMenu === null) return [];
        const menuIndex = actionMenu.index;
        const menuItem = items[menuIndex];
        if (!menuItem) return [];
        const menuRowKey = menuItem.quote_line_item_id || `${menuItem.product_id}-${menuItem.sku_id}-${menuIndex}`;
        const menuNoteOpen = openNotes.has(menuRowKey);
        // A plain array (not a Fragment) - Menu clones each direct child and a Fragment child
        // breaks that.
        return [
          <MenuItem key="details" aria-label={`View details for line ${menuIndex + 1}`} onClick={() => { setDetailIndex(menuIndex); setActionMenu(null); }}>
            <InfoOutlinedIcon fontSize="small" /><ListItemText primary="View Details" />
          </MenuItem>,
          ...(editable ? (() => {
            const hasDates = menuItem.inherits_section_dates === false || Boolean(menuItem.renewal_date);
            return [
              <MenuItem key="dates" aria-label={`${hasDates ? "Edit" : "Set"} dates for line ${menuIndex + 1}`} onClick={() => { setDateOverrideIndex(menuIndex); setActionMenu(null); }}>
                <EventOutlinedIcon fontSize="small" /><ListItemText primary={hasDates ? "Edit Dates" : "Set Dates"} />
              </MenuItem>,
            ];
          })() : []),
          // Manual entry, always reachable independent of promo_rejected or what the automatic
          // Partner Center check found - staff can still key one in by hand after declining the
          // system's own suggestion, or when the system won't offer one at all. Hidden once a
          // promotion is actually attached (promo_source set), whether found automatically or
          // entered here before - editing that one happens inline in the Promo column instead.
          ...(editable && !menuItem.promo_source && menuItem.adjustment_reason !== "product_restriction" && isCheckableLine(menuItem) && promoSyncOk ? [
            <MenuItem key="add-promo" aria-label={`Add a manual promotion for line ${menuIndex + 1}`} onClick={() => { setManualPromoIndex(menuIndex); setActionMenu(null); }}>
              <LocalOfferOutlinedIcon fontSize="small" /><ListItemText primary="Add Promo" />
            </MenuItem>,
          ] : []),
          ...((editable || menuItem.note) ? [
            <MenuItem key="notes" aria-label={`${editable ? "Edit" : "View"} notes for line ${menuIndex + 1}`} onClick={() => { toggleNote(menuRowKey); setActionMenu(null); }}>
              <EditNoteOutlinedIcon fontSize="small" /><ListItemText primary={menuNoteOpen ? "Hide Notes" : editable ? "Edit Notes" : "View Notes"} />
            </MenuItem>,
          ] : []),
          ...(editable ? [
            <MenuItem key="remove" aria-label={`Remove line ${menuIndex + 1}`} onClick={() => { onChange(items.filter((_, itemIndex) => itemIndex !== menuIndex)); setActionMenu(null); }}>
              <DeleteOutlineIcon fontSize="small" /><ListItemText primary="Remove" />
            </MenuItem>,
          ] : []),
        ];
      })()}
    </Menu>
    <Dialog open={prerequisiteIndex !== null} onClose={() => setPrerequisiteIndex(null)} fullWidth maxWidth="sm" aria-labelledby="prerequisites-title">
      <DialogTitle id="prerequisites-title">Prerequisites</DialogTitle>
      <DialogContent dividers>{prerequisiteIndex !== null && items[prerequisiteIndex] && (() => {
        const promoItem = items[prerequisiteIndex];
        return <>
          <DialogContentText>
            The promotion on {promoItem.sku_title} requires the customer to own one of these products.
            Choose one to add it to this quote.
          </DialogContentText>
          <List>{prerequisiteChoices(prerequisiteIndex, promoDescriptors[prerequisiteIndex]).map(choice =>
            <ListItemButton key={`${choice.product_id}/${choice.sku_id}`}
              onClick={() => { addPrerequisite(prerequisiteIndex, choice); setPrerequisiteIndex(null); }}>
              <ListItemText primary={choice.sku_title}
                secondary={`${promoItem.segment}, ${prerequisiteTerm(promoItem, choice)}, quantity ${promoItem.quantity}`} />
            </ListItemButton>)}
          </List>
        </>;
      })()}</DialogContent>
      <DialogActions><Button variant="outlined" onClick={() => setPrerequisiteIndex(null)}>Cancel</Button></DialogActions>
    </Dialog>
    {editable && onAdd && <Box className="add-line-action">{productCatalogLoading
      ? <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 1 }}><CircularProgress size={18} /><Typography variant="body2">Loading products...</Typography></Box>
      : <Tooltip describeChild enterDelay={1800} title="Add another product to the quote."><Button startIcon={<AddIcon />} onClick={onAdd}>Add Line Item</Button></Tooltip>}</Box>}
    <Dialog open={detailIndex !== null} onClose={() => setDetailIndex(null)} fullWidth maxWidth="sm">
      <DialogTitle>Offer Details</DialogTitle>
      <DialogContent dividers>{detailIndex !== null && (() => {
        const item = items[detailIndex];
        const isCustomProduct = item.product_id.startsWith("custom:");
        const isManualPrice = item.is_price_manual;
        const requiresManualPrice = item.is_price_unavailable;
        const product = rows.find(row => row.product_id === item.product_id && row.sku_id === item.sku_id && row.segment === item.segment && quoteProductTerm(row.term_duration, row.billing_plan) === item.term_duration);
        const detailTotal = item.sale_unit_price == null ? null : calculateQuoteLinePricing(item.sale_unit_price, item.cost_unit_price, item.quantity).total;
        const detailMargin = item.sale_unit_price == null || item.cost_unit_price == null ? null : item.sale_unit_price === 0 ? 0
          : (item.sale_unit_price - item.cost_unit_price) / item.sale_unit_price * 100;
        const details: [string, string | number][] = [
          ["Offer", item.sku_title], ["Type", requiresManualPrice ? "Custom pricing required" : isCustomProduct ? "Custom line item" : isManualPrice ? "Manually priced offer" : product ? "Catalog offer" : "Unmatched offer"],
          ...(!isCustomProduct ? [["Product ID", item.product_id], ["SKU ID", item.sku_id]] as [string, string][] : []),
          ["Segment", item.segment], ["Term", item.term_duration], ["Renewal", displayDate(item.renewal_date) || "Not available"],
          // Requested vs. applied Discount plus the plain-language reason (Issue 23) once the line
          // has been priced and saved. Before that: a manual line shows the Discount % the user
          // entered (Issue 36); an automatic line shows the quote's standard term Discount.
          ...(item.applied_discount_pct != null ? [
            ["Requested Discount", `${item.requested_discount_pct}%`],
            ["Applied Discount", `${item.applied_discount_pct}%`],
            ...(adjustmentMessage(item.adjustment_reason, isManualPrice) ? [["Discount Adjustment", adjustmentMessage(item.adjustment_reason, isManualPrice)!]] : []),
          ] as [string, string][] : [["Discount", isManualPrice
            ? (item.requested_discount_pct != null ? `${item.requested_discount_pct}%` : "Not set")
            : (item.term_duration ? `${discounts[item.term_duration]}%` : "Not available")]] as [string, string][]),
          ["Billing plan", product?.billing_plan || "Not available"], ["Quantity", item.quantity],
          ["Pricing", requiresManualPrice ? "Manual pricing required" : isManualPrice ? "Manual" : "Automatic"],
          // Issue 35: promotion details (advisory). The Microsoft percent, the customer promo, and
          // the seat band never appear in a customer document.
          ...((() => {
            const descriptor = promoDescriptors[detailIndex];
            if (!descriptor?.has_promotion && !item.promo_source) return [] as [string, string][];
            // A manual entry (staff's own deliberate, explicit override) never defers to the
            // automatic descriptor for ANY of its own fields - same rule as promoStatus/promoCap
            // above, for the same reason: the automatic check can still be reporting a completely
            // different promotion for this line, and none of its numbers, name, or eligibility
            // belong to what was actually entered here.
            const manual = item.promo_source === "manual";
            const microsoftPct = manual ? item.microsoft_promo_pct : (descriptor?.microsoft_promo_pct ?? item.microsoft_promo_pct);
            const endDate = manual ? item.promo_end_date : (descriptor?.promo_end_date ?? item.promo_end_date);
            const minQty = manual ? item.promo_min_quantity : (descriptor?.promo_min_quantity ?? item.promo_min_quantity);
            const maxQty = manual ? item.promo_max_quantity : (descriptor?.promo_max_quantity ?? item.promo_max_quantity);
            const eligibility = manual ? item.promo_eligibility : (descriptor?.eligibility ?? item.promo_eligibility);
            const detail = manual ? item.promo_eligibility_detail : (descriptor?.eligibility_detail ?? item.promo_eligibility_detail);
            return [
              // A saved quote has no live check - its stored name and prerequisite assessment
              // (migration 029) stand in, so Details shows the same picture after a reload.
              ["Promotion", manual ? "Manually entered" : (descriptor?.promotion_name || item.promo_name || "Partner Center promotion")],
              ["Promotion source", manual ? "Manual" : "Partner Center"],
              ["Microsoft discount", microsoftPct != null ? `${microsoftPct}%` : "Not available"],
              ["Customer promo", item.promo_source ? `${item.customer_promo_pct ?? 0}%` : "Not set"],
              ["Promotion ends", displayDate(endDate) || "No end date"],
              ...(minQty != null || maxQty != null ? [["Seat band", `${minQty ?? 1} to ${maxQty ?? "any"}`]] as [string, string][] : []),
              ...(!manual && descriptor?.first_purchase ? [["First purchase only", "Yes"]] as [string, string][] : []),
              ...((): [string, string][] => {
                if (manual) return [];
                const skus = descriptor ? descriptor.prerequisite_skus : item.promo_prerequisite_skus;
                const status = descriptor ? descriptor.prerequisite_status : item.promo_prerequisite_status;
                return skus?.length ? [["Prerequisite", prerequisiteSummary(skus, status)]] : [];
              })(),
              // The complete view: every reason or note the assessment carries, in every state -
              // the status row under the line only shows them when something is unconfirmed.
              ["Eligibility", (() => {
                const reason = eligibilityReason(detail);
                return eligibility === "requirements_unmet" ? `Requirements not met${reason ? `: ${reason}` : ""}`
                  : eligibility === "unverified" ? (reason ? `Not verified: ${reason}` : "Not verified - confirm in Partner Center")
                  : eligibility === "met" ? `Requirements met${reason ? ` (${reason})` : ""}` : "Not checked";
              })()],
            ] as [string, string][];
          })()),
          // Issue 33: the full proration calculation - charge dates, billable-day count, and (when
          // both boundaries are partial) each component's own day count and per-seat Sale.
          ...((): [string, string][] => {
            if (!item.charge_start_date) return [];
            // 2026-10-05: every line carries its full price (monthly for P1M / P1YM, annual for
            // P1YA) - the MSRP/Sale/Total rows below. Its prorated bills (a partial first / last
            // month, or a prorated P1YA year) are listed here, and Full-Term Sub-Total is what the whole term
            // costs - the same figures the quote documents print.
            const rows: [string, string][] = [
              ["Charge start", formatIsoDate(item.charge_start_date)],
              ["Charge end", formatIsoDate(item.charge_end_date)],
              ["Billable days", `${item.billable_days ?? "-"} of ${item.full_period_days ?? "-"}`],
            ];
            try {
              const components = JSON.parse(item.proration_detail || "[]") as {
                start: string; end: string; billable_days: number; full_period_days: number; sale_unit: number;
              }[];
              const annual = item.term_duration === "P1YA";
              const prorated = item.billable_days != null && item.full_period_days != null && item.billable_days < item.full_period_days;
              const partial = (c: typeof components[number] | undefined) => c != null && c.billable_days < c.full_period_days;
              const bill = (c: typeof components[number]) =>
                `${formatIsoDate(c.start)} - ${formatIsoDate(c.end)}: ${c.billable_days}/${c.full_period_days} days, ${money(c.sale_unit * item.quantity)}`;
              if (annual && partial(components[0])) rows.push(["Prorated bill", bill(components[0])]);
              if (!annual && partial(components[0])) rows.push(["First month bill (prorated)", bill(components[0])]);
              if (!annual && components.length > 1 && partial(components[components.length - 1]))
                rows.push(["Last month bill (prorated)", bill(components[components.length - 1])]);
              const termCost = prorated && components.length > 0
                ? components.reduce((sum, c) => sum + c.sale_unit * item.quantity, 0)
                : (item.total_price ?? 0) * (item.term_duration === "P1YM" ? 12 : 1);
              rows.push(["Full-Term Sub-Total", money(termCost)]);
              if (components.length > 1) components.forEach((c, index) => rows.push([
                `Component ${index + 1}`,
                `${formatIsoDate(c.start)} - ${formatIsoDate(c.end)}: ${c.billable_days}/${c.full_period_days} days, Sale ${unitMoney(c.sale_unit)}`,
              ]));
            } catch { /* malformed detail - show the summary only */ }
            return rows;
          })(),
          ["MSRP", unitMoney(item.msrp_unit_price) || "Not available"], ["Cost", unitMoney(item.cost_unit_price) || "Not available"], ["Sale", unitMoney(item.sale_unit_price) || "Not available"], ["Total", money(detailTotal) || "Not available"],
          ["Margin", detailMargin == null ? "Not available" : `${detailMargin.toFixed(2)}%`], ["Notes", item.note || "Not available"],
        ];
        return <Box component="dl" className="product-detail-list">{details.map(([label, value]) => <Box key={label}><Typography component="dt">{label}</Typography><Typography component="dd">{value}</Typography></Box>)}</Box>;
      })()}</DialogContent>
      <DialogActions><Button variant="outlined" onClick={() => setDetailIndex(null)}>Close</Button></DialogActions>
    </Dialog>
    <PromotionsCatalogDialog open={promotionsCatalogOpen} onClose={() => setPromotionsCatalogOpen(false)}
      allowedSegments={segments.length < allSegments.length ? segments : undefined}
      onSelect={editable ? applyPromotionRow : undefined} selectError={promoSelectError} customerGuid={customerGuid} />
    {dateOverrideIndex !== null && items[dateOverrideIndex] && <QuoteDatesModal open onClose={() => setDateOverrideIndex(null)}
      scope="line" quoteType={quoteType} customerGuid={customerGuid}
      term={(items[dateOverrideIndex].term_duration || "P1YA") as QuoteTerm}
      title={`Dates for ${items[dateOverrideIndex].sku_title || `line ${dateOverrideIndex + 1}`}`}
      value={lineDatesValue(items[dateOverrideIndex])}
      inheritsSectionDates={items[dateOverrideIndex].inherits_section_dates !== false}
      renewalDate={items[dateOverrideIndex].renewal_date ?? null}
      onConfirm={result => {
        applyLineDates(dateOverrideIndex, result.dates, result.inherits,
          result.renewalDate !== undefined ? { renewal_date: result.renewalDate } : {});
        setDateOverrideIndex(null);
      }} />}
    <ManualPromoDialog open={manualPromoIndex !== null} onClose={() => setManualPromoIndex(null)} onSubmit={promo => {
      if (manualPromoIndex !== null) update(manualPromoIndex, {
        promo_source: "manual", microsoft_promo_pct: promo.microsoft_promo_pct, customer_promo_pct: promo.customer_promo_pct,
        promo_end_date: promo.promo_end_date, promo_min_quantity: promo.promo_min_quantity, promo_max_quantity: promo.promo_max_quantity,
        promo_id: null, promo_eligibility: null, promo_eligibility_detail: null,
        promo_name: null, promo_prerequisite_skus: null, promo_prerequisite_status: null,
        // Add Promo is reachable from a previously-rejected line (staff keying one in by hand
        // after declining the automatic suggestion) - a deliberate manual entry is itself an
        // opt-in, same as typing a share percent directly already is elsewhere in this file.
        promo_rejected: false,
      });
      setManualPromoIndex(null);
    }} />
  </Box>;
};

export default QuoteLineItemGrid;
